import { CalculationTodo, InterpretationConfidenceLimitation, SajuValidationPayload } from './types';

export class SajuDerivedCalculator {
    constructor(private engine: any) {}

    public buildDerivedData(pillars: any[], luckInfo: any, targetYear?: number) {
        let hasTargetYear = targetYear !== undefined && targetYear !== null && String(targetYear) !== '';
        let year = hasTargetYear ? this.normalTargetYear(targetYear) : null;
        let pillarDetails = this.pillarDetails(pillars);
        let interactions = this.buildInteractionSections(pillars);
        let nobleStars = this.findNobleStars(pillars);
        let specialStars = this.findSpecialStars(pillars);
        let emptyBranches = this.emptyBranches(pillars[2].cycleIndex);

        return {
            pillarDetails: pillarDetails,
            interactions: interactions,
            nobleStars: nobleStars,
            specialStars: specialStars,
            emptyBranches: emptyBranches,
            luckFlow: this.majorLuckFlow(pillars, luckInfo),
            annualLuck: year ? this.annualLuckFlow(year, pillars) : [],
            monthlyLuck: year ? this.monthlyLuckFlow(year, pillars) : []
        };
    }

    public enrichValidationPayload(
        baseValidation: SajuValidationPayload,
        pillars: any[],
        elements: any[],
        tenGods: any[],
        luckInfo: any,
        derived: any
    ): SajuValidationPayload {
        let rawInput = baseValidation.rawInput || {};
        return {
            ...baseValidation,
            rawInput: {
                ...rawInput,
                name: rawInput['name'] || '',
                birthDate: rawInput['birthDate'] || '',
                birthTime: rawInput['birthTime'] || '12:00',
                timeUnknown: !!rawInput['timeUnknown'],
                calendarType: rawInput['calendarType'] || 'solar',
                birthPlace: rawInput['birthPlace'] || 'none',
                gender: rawInput['gender'] || 'none',
                targetYear: rawInput['targetYear'] || null
            },
            pillars: this.validationPillars(pillars, derived.pillarDetails || []),
            tenGods: tenGods || [],
            hiddenStems: this.validationHiddenStems(pillars),
            twelveStages: this.validationTwelveStages(pillars),
            elementDistribution: elements || [],
            interactions: derived.interactions || [],
            nobleStars: derived.nobleStars || [],
            specialStars: derived.specialStars || [],
            emptyBranches: derived.emptyBranches || [],
            majorLuck: luckInfo || null,
            timeUnknownLimitations: this.timeUnknownLimitations(baseValidation),
            todos: this.calculationValidationTodos()
        };
    }

    public timeUnknownLimitations(validation: SajuValidationPayload): InterpretationConfidenceLimitation[] {
        let timeUnknown = !!(
            validation?.normalizedInput?.timeUnknown ||
            validation?.calculationOptions?.timeUnknown ||
            validation?.rawInput?.['timeUnknown']
        );
        if (!timeUnknown) return [];

        return [
            {
                key: 'hour_pillar',
                label: '시주·시지',
                confidence: 0,
                severity: 'warning',
                affectedAreas: ['시주', '시지', '시주 천간·지지'],
                evidenceIds: ['warning:time_unknown', 'pillar:hour:unknown'],
                reason: '출생 시간이 없어 시주와 시지를 계산하지 않습니다.',
                handling: '원국 표에서는 미정으로 표시하고, 해석 근거에서는 제외합니다.'
            },
            {
                key: 'hour_ten_god',
                label: '시주 십성·지장간',
                confidence: 0,
                severity: 'warning',
                affectedAreas: ['시주 천간 십성', '시지 지장간', '시지 십성'],
                evidenceIds: ['warning:time_unknown', 'tenGod:hour:unknown'],
                reason: '시주가 없으면 시간 천간과 시지 지장간의 십성을 확정할 수 없습니다.',
                handling: '십성 분포와 지장간 표는 년주·월주·일주 기준 참고값으로 낮춰 표시합니다.',
                referenceOnly: true
            },
            {
                key: 'hour_stars',
                label: '시지 기준 신살·귀인',
                confidence: 0.35,
                severity: 'warning',
                affectedAreas: ['시지 기준 신살', '시지 기준 귀인', '시주 공망 여부'],
                evidenceIds: ['warning:time_unknown', 'star:hour_branch:limited'],
                reason: '시지가 확정되지 않아 시주 위치에서 잡히는 신살과 귀인성은 누락될 수 있습니다.',
                handling: '계산된 년주·월주·일주 신살만 보여주고, 시지 신살 해석은 참고용으로 제한합니다.',
                referenceOnly: true
            },
            {
                key: 'hour_interactions',
                label: '시주 포함 합충형파해',
                confidence: 0.35,
                severity: 'warning',
                affectedAreas: ['시주 천간합·충', '시지 합충형파해', '시지 원진'],
                evidenceIds: ['warning:time_unknown', 'interaction:hour:limited'],
                reason: '시주가 빠지면 시주와 다른 기둥 사이의 합충형파해를 감지할 수 없습니다.',
                handling: '합충형파해는 년주·월주·일주 사이의 관계만 확정값으로 봅니다.',
                referenceOnly: true
            },
            {
                key: 'later_life_children',
                label: '말년운·자녀운',
                confidence: 0.3,
                severity: 'warning',
                affectedAreas: ['말년운', '자녀운', '결실의 자리'],
                evidenceIds: ['warning:time_unknown', 'palace:hour:limited'],
                reason: '시주는 말년운과 자녀운을 보는 자리라 생시 미정에서는 강하게 말할 수 없습니다.',
                handling: '해당 풀이는 제외하거나 참고용 안내로만 표시합니다.',
                referenceOnly: true
            },
            {
                key: 'career_partial',
                label: '직업·결실 일부 해석',
                confidence: 0.6,
                severity: 'info',
                affectedAreas: ['일의 결실', '후반부 역할', '자녀·제자·산출물 관련 직업 해석'],
                evidenceIds: ['warning:time_unknown', 'career:hour_related:limited'],
                reason: '직업 해석 중 시주가 보조하는 결실·산출물·후반부 역할 신호는 확인할 수 없습니다.',
                handling: '직업 챕터는 일간·월지·중심 십성 중심으로 읽고 시주 보조근거는 제외합니다.',
                referenceOnly: true
            },
            {
                key: 'major_luck_timing',
                label: '대운 시작 세부시점',
                confidence: 0.75,
                severity: 'info',
                affectedAreas: ['대운 시작의 세부 시각', '절기까지 시간 차'],
                evidenceIds: ['warning:time_unknown', 'majorLuck:timeUnknown:true'],
                reason: '생시 미정에서는 정오 기준으로 절기까지의 시간 차를 계산합니다.',
                handling: '대운 방향은 유지하되 시작 시점은 약식 표기로 보여줍니다.',
                referenceOnly: true
            }
        ];
    }

    public pillarDetails(pillars: any[]) {
        let dayStem = pillars[2].stem;
        let empty = this.emptyBranches(pillars[2].cycleIndex);
        let displayOrder = [pillars[3], pillars[2], pillars[1], pillars[0]];

        return displayOrder.map((pillar: any) => {
            if (pillar?.unknown) {
                return {
                    ...pillar,
                    stemTenGod: '미정',
                    branchTenGod: '미정',
                    branchTenGods: [],
                    hiddenText: '미정',
                    dayShinsal: '미정',
                    yearShinsal: '미정',
                    starText: '생시 미정',
                    twelveStage: '',
                    nayin: '',
                    empty: false
                };
            }
            let hidden = this.engine.hiddenStems(pillar.branch.name).map((item: any) => ({
                ...item,
                tenGod: this.engine.tenGod(dayStem, item.stem)
            }));
            return {
                ...pillar,
                stemTenGod: pillar.part === '일주' ? '일간' : this.engine.tenGod(dayStem, pillar.stem),
                branchTenGod: hidden[0]?.tenGod || '',
                branchTenGods: hidden,
                hiddenText: hidden.map((item: any) => `${item.stem.name}${item.tenGod}`).join(' · '),
                dayShinsal: this.shinsalName(pillars[2].branch.name, pillar.branch.name),
                yearShinsal: this.shinsalName(pillars[0].branch.name, pillar.branch.name),
                starText: this.pillarStarText(pillar, pillars),
                twelveStage: this.engine.twelveStage(dayStem, pillar.branch.name),
                nayin: this.nayin(pillar.cycleIndex),
                empty: empty.indexOf(pillar.branch.name) >= 0
            };
        });
    }

    public validationPillars(pillars: any[], pillarDetails: any[]) {
        return (pillars || []).map((pillar: any) => {
            let decorated = (pillarDetails || []).find((item: any) => item.part === pillar.part) || {};
            return {
                part: pillar.part,
                label: pillar.label,
                stem: pillar?.unknown ? '미정' : `${pillar.stem.name}${pillar.stem.hanja}`,
                branch: pillar?.unknown ? '미정' : `${pillar.branch.name}${pillar.branch.hanja}`,
                stemTenGod: decorated.stemTenGod || (pillar.part === '일주' ? '일간' : ''),
                branchTenGod: decorated.branchTenGod || '',
                hiddenText: decorated.hiddenText || (pillar?.unknown ? '미정' : ''),
                twelveStage: decorated.twelveStage || '',
                starText: decorated.starText || '',
                unknown: !!pillar?.unknown
            };
        });
    }

    public validationHiddenStems(pillars: any[]) {
        return (pillars || []).map((pillar: any) => ({
            part: pillar.part,
            branch: pillar?.unknown ? '미정' : `${pillar.branch.name}${pillar.branch.hanja}`,
            items: pillar?.unknown ? [] : this.engine.hiddenStems(pillar.branch.name).map((hidden: any) => ({
                stem: `${hidden.stem.name}${hidden.stem.hanja}`,
                role: hidden.role,
                weight: hidden.weight,
                tenGod: this.engine.tenGod(pillars[2].stem, hidden.stem)
            }))
        }));
    }

    public validationTwelveStages(pillars: any[]) {
        let dayStem = pillars?.[2]?.stem;
        return (pillars || []).map((pillar: any) => ({
            part: pillar.part,
            branch: pillar?.unknown ? '미정' : `${pillar.branch.name}${pillar.branch.hanja}`,
            stage: pillar?.unknown ? '미정' : this.engine.twelveStage(dayStem, pillar.branch.name)
        }));
    }

    public calculationValidationTodos(): CalculationTodo[] {
        return [
            { label: '합화 성립 조건', reason: '천간합 감지는 있으나 계절·통근 기반 합화 성립 판정은 아직 분리되지 않았습니다.' },
            { label: '귀문관살', reason: '현재 합충형파해 표시에는 귀문 관계가 별도 계산값으로 포함되지 않았습니다.' },
            { label: '용신·희신·기신 확정', reason: '현재는 보완 기운 후보만 제공하며 확정 판단은 gold sample 이후 분리해야 합니다.' },
            { label: '해외 출생/진태양시', reason: '현재 시간 보정은 국내 주요 지역 경도 보정과 한국 서머타임 baseline에 제한됩니다.' }
        ];
    }

    public shinsalName(baseBranch: string, targetBranch: string) {
        return this.twelveShinsalTable(baseBranch)[targetBranch] || '';
    }

    public pillarStarText(pillar: any, pillars: any[]) {
        if (!pillar || pillar.unknown) return '생시 미정';
        let dayStem = pillars[2].stem.name;
        let monthBranch = pillars[1].branch.name;
        let empty = this.emptyBranches(pillars[2].cycleIndex);
        let stars: string[] = [];
        let branch = pillar.branch.name;
        let stem = pillar.stem.name;
        let cheoneul: any = {
            갑: ['축', '미'], 무: ['축', '미'], 경: ['축', '미'],
            을: ['자', '신'], 기: ['자', '신'],
            병: ['해', '유'], 정: ['해', '유'],
            신: ['인', '오'],
            임: ['사', '묘'], 계: ['사', '묘']
        };
        let taegeuk: any = {
            갑: ['자', '오'], 을: ['자', '오'],
            병: ['묘', '유'], 정: ['묘', '유'],
            무: ['진', '술', '축', '미'], 기: ['진', '술', '축', '미'],
            경: ['인', '해'], 신: ['인', '해'],
            임: ['사', '신'], 계: ['사', '신']
        };
        let munchang: any = { 갑: ['사'], 을: ['오'], 병: ['신'], 정: ['유'], 무: ['신'], 기: ['유'], 경: ['해'], 신: ['자'], 임: ['인'], 계: ['묘'] };
        let cheondeok: any = { 인: '정', 묘: '신', 진: '임', 사: '신', 오: '해', 미: '갑', 신: '계', 유: '인', 술: '병', 해: '을', 자: '사', 축: '경' };
        let woldeok: any = { 인: '병', 오: '병', 술: '병', 신: '임', 자: '임', 진: '임', 사: '경', 유: '경', 축: '경', 해: '갑', 묘: '갑', 미: '갑' };
        if ((cheoneul[dayStem] || []).indexOf(branch) >= 0) stars.push('천을귀인');
        if ((taegeuk[dayStem] || []).indexOf(branch) >= 0) stars.push('태극귀인');
        if ((munchang[dayStem] || []).indexOf(branch) >= 0) stars.push('문창귀인');
        if (cheondeok[monthBranch] === stem || cheondeok[monthBranch] === branch) stars.push('천덕귀인');
        if (woldeok[monthBranch] === stem || woldeok[monthBranch] === branch) stars.push('월덕귀인');
        if (empty.indexOf(branch) >= 0) stars.push('공망');
        let dayShinsal = this.shinsalName(pillars[2].branch.name, branch);
        if (['도화살', '역마살', '화개살'].indexOf(dayShinsal) >= 0) stars.push(dayShinsal);
        return stars.length ? stars.join(' · ') : '-';
    }

    public buildInteractionSections(pillars: any[]) {
        let known = pillars.filter((pillar: any) => !pillar?.unknown);
        let sections = [
            {
                label: '궁성',
                items: known.map((pillar: any) => `${pillar.part}: ${this.palaceTitle(pillar.part)} · ${this.palaceNote(pillar.part)}`)
            },
            { label: '천간합', items: this.findStemPairInteractions(known, this.stemCombinePairs(), '합') },
            { label: '천간충', items: this.findStemPairInteractions(known, this.stemClashPairs(), '충') },
            { label: '지지육합', items: this.findBranchPairInteractions(known, this.branchSixCombinePairs(), '육합') },
            { label: '지지삼합', items: this.findBranchGroupInteractions(known, this.branchTriadGroups(), '삼합') },
            { label: '지지방합', items: this.findBranchGroupInteractions(known, this.branchDirectionGroups(), '방합') },
            { label: '지지충', items: this.findBranchPairInteractions(known, this.branchClashPairs(), '충') },
            { label: '형', items: this.findBranchGroupInteractions(known, this.branchPenaltyGroups(), '형') },
            { label: '파', items: this.findBranchPairInteractions(known, this.branchBreakPairs(), '파') },
            { label: '해', items: this.findBranchPairInteractions(known, this.branchHarmPairs(), '해') },
            { label: '원진', items: this.findBranchPairInteractions(known, this.branchWonjingPairs(), '원진') },
            { label: '공망', items: this.emptyInteractionItems(known, pillars[2].cycleIndex) }
        ];
        return sections.map((section: any) => ({
            ...section,
            active: section.items.length > 0
        }));
    }

    public findStemPairInteractions(pillars: any[], pairMap: any, suffix: string) {
        let items: string[] = [];
        for (let pair of this.pillarPairs(pillars)) {
            let forwardKey = `${pair.a.stem.name}${pair.b.stem.name}`;
            let reverseKey = `${pair.b.stem.name}${pair.a.stem.name}`;
            let hasMatch = Object.prototype.hasOwnProperty.call(pairMap, forwardKey) || Object.prototype.hasOwnProperty.call(pairMap, reverseKey);
            if (hasMatch) {
                let label = pairMap[forwardKey] || pairMap[reverseKey] || '';
                items.push(`${pair.a.part}-${pair.b.part} ${pair.a.stem.name}${pair.b.stem.name} ${label}${suffix}`);
            }
        }
        return items;
    }

    public findBranchPairInteractions(pillars: any[], pairMap: any, suffix: string) {
        let items: string[] = [];
        for (let pair of this.pillarPairs(pillars)) {
            let forwardKey = `${pair.a.branch.name}${pair.b.branch.name}`;
            let reverseKey = `${pair.b.branch.name}${pair.a.branch.name}`;
            let hasMatch = Object.prototype.hasOwnProperty.call(pairMap, forwardKey) || Object.prototype.hasOwnProperty.call(pairMap, reverseKey);
            if (hasMatch) {
                let label = pairMap[forwardKey] || pairMap[reverseKey] || '';
                items.push(`${pair.a.part}-${pair.b.part} ${pair.a.branch.name}${pair.b.branch.name} ${label}${suffix}`);
            }
        }
        return items;
    }

    public findBranchGroupInteractions(pillars: any[], groups: any[], suffix: string) {
        let items: string[] = [];
        for (let group of groups) {
            let matched = group.branches
                .map((branch: string) => pillars.find((pillar: any) => pillar.branch.name === branch))
                .filter((pillar: any) => !!pillar);
            if (matched.length >= 2) {
                let complete = matched.length === group.branches.length ? '성립' : '부분';
                items.push(`${matched.map((pillar: any) => `${pillar.part} ${pillar.branch.name}`).join(' · ')} ${group.name}${suffix} ${complete}`);
            }
        }
        return items;
    }

    public emptyInteractionItems(pillars: any[], dayCycleIndex: number) {
        let empty = this.emptyBranches(dayCycleIndex);
        return pillars
            .filter((pillar: any) => empty.indexOf(pillar.branch.name) >= 0)
            .map((pillar: any) => `${pillar.part} ${pillar.branch.name} 공망`);
    }

    public pillarPairs(pillars: any[]) {
        let pairs: any[] = [];
        for (let i = 0; i < pillars.length; i++) {
            for (let j = i + 1; j < pillars.length; j++) {
                pairs.push({ a: pillars[i], b: pillars[j] });
            }
        }
        return pairs;
    }

    public stemCombinePairs() {
        return { 갑기: '토', 을경: '금', 병신: '수', 정임: '목', 무계: '화' };
    }

    public stemClashPairs() {
        return { 갑경: '', 을신: '', 병임: '', 정계: '' };
    }

    public branchSixCombinePairs() {
        return { 자축: '토', 인해: '목', 묘술: '화', 유진: '금', 사신: '수', 미오: '토' };
    }

    public branchClashPairs() {
        return { 오자: '', 미축: '', 신인: '', 유묘: '', 술진: '', 해사: '' };
    }

    public branchBreakPairs() {
        return { 유자: '', 진축: '', 해인: '', 오묘: '', 신사: '', 술미: '' };
    }

    public branchHarmPairs() {
        return { 미자: '', 오축: '', 해인: '', 진묘: '', 해신: '', 술유: '' };
    }

    public branchWonjingPairs() {
        return { 미자: '', 오축: '', 유인: '', 신묘: '', 해진: '', 술사: '' };
    }

    public branchTriadGroups() {
        return [
            { name: '수국', branches: ['신', '자', '진'] },
            { name: '목국', branches: ['해', '묘', '미'] },
            { name: '화국', branches: ['인', '오', '술'] },
            { name: '금국', branches: ['사', '유', '축'] }
        ];
    }

    public branchDirectionGroups() {
        return [
            { name: '목국', branches: ['인', '묘', '진'] },
            { name: '화국', branches: ['사', '오', '미'] },
            { name: '금국', branches: ['신', '유', '술'] },
            { name: '수국', branches: ['해', '자', '축'] }
        ];
    }

    public branchPenaltyGroups() {
        return [
            { name: '무은지형', branches: ['인', '사', '신'] },
            { name: '지세지형', branches: ['축', '술', '미'] },
            { name: '무례지형', branches: ['자', '묘'] },
            { name: '자형', branches: ['진', '진'] },
            { name: '자형', branches: ['오', '오'] },
            { name: '자형', branches: ['유', '유'] },
            { name: '자형', branches: ['해', '해'] }
        ];
    }

    public findNobleStars(pillars: any[]) {
        let dayStem = pillars[2].stem.name;
        let monthBranch = pillars[1].branch.name;
        let branchLocations = this.pillarBranchLocations(pillars);
        let stemLocations = this.pillarStemLocations(pillars);
        let stars: any[] = [];
        let addBranchStar = (name: string, targets: string[], description: string) => {
            let matches = targets
                .filter((branch: string) => branchLocations[branch])
                .map((branch: string) => `${branchLocations[branch].join(', ')} ${branch}`);
            if (matches.length) {
                stars.push({ name: name, target: targets.join('·'), location: matches.join(' / '), description: description });
            }
        };
        let addStemOrBranchStar = (name: string, target: string, description: string) => {
            let locations: string[] = [];
            if (stemLocations[target]) locations = locations.concat(stemLocations[target].map((part: string) => `${part} 천간 ${target}`));
            if (branchLocations[target]) locations = locations.concat(branchLocations[target].map((part: string) => `${part} 지지 ${target}`));
            if (locations.length) stars.push({ name: name, target: target, location: locations.join(' / '), description: description });
        };

        let cheoneul: any = {
            갑: ['축', '미'], 무: ['축', '미'], 경: ['축', '미'],
            을: ['자', '신'], 기: ['자', '신'],
            병: ['해', '유'], 정: ['해', '유'],
            신: ['인', '오'],
            임: ['사', '묘'], 계: ['사', '묘']
        };
        let taegeuk: any = {
            갑: ['자', '오'], 을: ['자', '오'],
            병: ['묘', '유'], 정: ['묘', '유'],
            무: ['진', '술', '축', '미'], 기: ['진', '술', '축', '미'],
            경: ['인', '해'], 신: ['인', '해'],
            임: ['사', '신'], 계: ['사', '신']
        };
        let munchang: any = { 갑: ['사'], 을: ['오'], 병: ['신'], 정: ['유'], 무: ['신'], 기: ['유'], 경: ['해'], 신: ['자'], 임: ['인'], 계: ['묘'] };
        let cheondeok: any = { 인: '정', 묘: '신', 진: '임', 사: '신', 오: '해', 미: '갑', 신: '계', 유: '인', 술: '병', 해: '을', 자: '사', 축: '경' };
        let woldeok: any = { 인: '병', 오: '병', 술: '병', 신: '임', 자: '임', 진: '임', 사: '경', 유: '경', 축: '경', 해: '갑', 묘: '갑', 미: '갑' };

        addBranchStar('천을귀인', cheoneul[dayStem] || [], '도움과 보호를 받는 귀인성으로 해석합니다.');
        addBranchStar('태극귀인', taegeuk[dayStem] || [], '학습, 직관, 회복력을 살리는 길성으로 봅니다.');
        addBranchStar('문창귀인', munchang[dayStem] || [], '글, 말, 기획, 공부 감각을 돕는 별입니다.');
        addStemOrBranchStar('천덕귀인', cheondeok[monthBranch], '월지를 기준으로 보는 덕성의 귀인입니다.');
        addStemOrBranchStar('월덕귀인', woldeok[monthBranch], '월지 삼합에서 오는 온건한 길성으로 봅니다.');

        return stars;
    }

    public findSpecialStars(pillars: any[]) {
        let stars: any[] = [];
        let bases = [
            { label: '일지', branch: pillars[2].branch.name },
            { label: '년지', branch: pillars[0].branch.name }
        ];
        let empty = this.emptyBranches(pillars[2].cycleIndex);

        for (let basis of bases) {
            let table = this.twelveShinsalTable(basis.branch);
            for (let pillar of pillars) {
                if (pillar?.unknown) continue;
                let name = table[pillar.branch.name];
                if (!name) continue;
                stars.push({
                    name: name,
                    basis: `${basis.label} ${basis.branch} 기준`,
                    location: `${pillar.part} ${pillar.branch.name}`,
                    description: this.specialStarTone(name)
                });
            }
        }

        for (let pillar of pillars) {
            if (pillar?.unknown) continue;
            if (empty.indexOf(pillar.branch.name) >= 0) {
                stars.push({
                    name: '공망',
                    basis: `${pillars[2].label} 일주 기준`,
                    location: `${pillar.part} ${pillar.branch.name}`,
                    description: '비어 있는 자리로 보아 기대와 실제 사이의 간극을 점검합니다.'
                });
            }
        }

        let seen: any = {};
        return stars.filter((star: any) => {
            let key = `${star.name}|${star.basis}|${star.location}`;
            if (seen[key]) return false;
            seen[key] = true;
            return true;
        });
    }

    public majorLuckFlow(pillars: any[], luckInfo: any, length: number = 8) {
        let monthIndex = pillars[1].cycleIndex;
        return Array.from({ length: length }).map((_: any, index: number) => {
            let step = luckInfo.direction === '순행' ? index + 1 : -(index + 1);
            let ganji = this.engine.ganjiFromIndex(monthIndex + step);
            return {
                age: luckInfo.startAge + index * 10,
                pillar: ganji.label,
                tenGod: this.engine.tenGod(pillars[2].stem, ganji.stem),
                note: this.engine.elementTone(ganji.stem.element).core
            };
        });
    }

    public annualLuckFlow(startYear: number, pillars: any[], length: number = 8) {
        let dayStem = pillars[2].stem;
        let empty = this.emptyBranches(pillars[2].cycleIndex);
        return Array.from({ length: length }).map((_: any, index: number) => {
            let year = startYear + index;
            let ganji = this.engine.ganjiFromIndex(year - 4);
            return {
                year: year,
                pillar: ganji.label,
                tenGod: this.engine.tenGod(dayStem, ganji.stem),
                element: ganji.stem.element,
                empty: empty.indexOf(ganji.branch.name) >= 0,
                note: this.engine.elementTone(ganji.stem.element).action
            };
        });
    }

    public monthlyLuckFlow(targetYear: number, pillars: any[]) {
        let dayStem = pillars[2].stem;
        return this.engine.solarMonthTerms(targetYear).map((term: any) => {
            let basisYear = term.index < 2 ? targetYear - 1 : targetYear;
            let yearPillar = this.engine.ganjiFromIndex(basisYear - 4);
            let monthStemIndex = this.engine.monthStemIndex(yearPillar.stem.index, term.branchIndex);
            let ganji = this.engine.pillar('', monthStemIndex, term.branchIndex);
            return {
                month: `${term.date.getMonth() + 1}월`,
                term: term.name,
                termDate: this.engine.formatDateTime(term.date),
                pillar: ganji.label,
                tenGod: this.engine.tenGod(dayStem, ganji.stem),
                note: this.engine.elementTone(ganji.stem.element).action
            };
        });
    }

    public emptyBranches(dayCycleIndex: number) {
        let start = Math.floor(this.engine.mod(dayCycleIndex, 60) / 10) * 10;
        return [
            this.engine.branchData()[this.engine.mod(start + 10, 12)].name,
            this.engine.branchData()[this.engine.mod(start + 11, 12)].name
        ];
    }

    public twelveShinsalTable(baseBranch: string) {
        let groups: any = {
            인오술: ['해', '자', '축', '인', '묘', '진', '사', '오', '미', '신', '유', '술'],
            사유축: ['인', '묘', '진', '사', '오', '미', '신', '유', '술', '해', '자', '축'],
            신자진: ['사', '오', '미', '신', '유', '술', '해', '자', '축', '인', '묘', '진'],
            해묘미: ['신', '유', '술', '해', '자', '축', '인', '묘', '진', '사', '오', '미']
        };
        let labels = ['겁살', '재살', '천살', '지살', '도화살', '월살', '망신살', '장성살', '반안살', '역마살', '육해살', '화개살'];
        let groupKey = Object.keys(groups).find((key: string) => key.indexOf(baseBranch) >= 0) || '신자진';
        let table: any = {};
        groups[groupKey].forEach((branch: string, index: number) => table[branch] = labels[index]);
        return table;
    }

    public specialStarTone(name: string) {
        let tones: any = {
            겁살: '경쟁과 돌파 욕구가 강해지는 자리입니다.',
            재살: '현실 문제, 돈과 책임을 점검하는 자리입니다.',
            천살: '외부 환경의 변수를 크게 느끼는 자리입니다.',
            지살: '이동, 배움, 활동 반경 확장을 뜻합니다.',
            도화살: '매력, 인기, 표현력이 드러나는 자리입니다.',
            월살: '멈춤과 재정비, 마음의 정리가 필요한 자리입니다.',
            망신살: '평판과 말의 파장을 조심해야 하는 자리입니다.',
            장성살: '주도권, 리더십, 승부성이 살아나는 자리입니다.',
            반안살: '성과, 안정, 인정운을 기대하는 자리입니다.',
            역마살: '이동, 출장, 변화, 새로운 판을 뜻합니다.',
            육해살: '작은 걸림돌과 컨디션 관리를 살피는 자리입니다.',
            화개살: '몰입, 예술성, 혼자 깊어지는 시간을 뜻합니다.'
        };
        return tones[name] || '명식에서 반복되는 흐름을 살펴보는 별입니다.';
    }

    public pillarBranchLocations(pillars: any[]) {
        let locations: any = {};
        for (let pillar of pillars) {
            if (pillar?.unknown) continue;
            let name = pillar.branch.name;
            if (!locations[name]) locations[name] = [];
            locations[name].push(pillar.part);
        }
        return locations;
    }

    public pillarStemLocations(pillars: any[]) {
        let locations: any = {};
        for (let pillar of pillars) {
            if (pillar?.unknown) continue;
            let name = pillar.stem.name;
            if (!locations[name]) locations[name] = [];
            locations[name].push(pillar.part);
        }
        return locations;
    }

    public nayin(cycleIndex: number) {
        let names = [
            '해중금', '해중금', '노중화', '노중화', '대림목', '대림목', '노방토', '노방토', '검봉금', '검봉금',
            '산두화', '산두화', '간하수', '간하수', '성두토', '성두토', '백랍금', '백랍금', '양류목', '양류목',
            '천중수', '천중수', '옥상토', '옥상토', '벽력화', '벽력화', '송백목', '송백목', '장류수', '장류수',
            '사중금', '사중금', '산하화', '산하화', '평지목', '평지목', '벽상토', '벽상토', '금박금', '금박금',
            '복등화', '복등화', '천하수', '천하수', '대역토', '대역토', '차천금', '차천금', '상자목', '상자목',
            '대계수', '대계수', '사중토', '사중토', '천상화', '천상화', '석류목', '석류목', '대해수', '대해수'
        ];
        return names[this.engine.mod(cycleIndex, 60)];
    }

    private palaceTitle(part: string) {
        let data: any = { 시주: '말년운', 일주: '중년운', 월주: '청년운', 년주: '초년운' };
        return data[part] || '';
    }

    private palaceNote(part: string) {
        let data: any = { 시주: '자녀운, 결실', 일주: '정체성, 자아', 월주: '부모, 사회상', 년주: '조상, 시대상' };
        return data[part] || '';
    }

    private normalTargetYear(value: any) {
        let year = parseInt(String(value || ''), 10);
        if (Number.isNaN(year)) year = new Date().getFullYear();
        return this.engine.clamp(year, 1900, 2100);
    }
}
